import {
  trackError,
  trackFeatureUsage,
  trackReportGeneration,
} from "../utils/analytics";

/**
 * Reportes: que se genera, con que rango y que se exporta.
 *
 * Los dos puntos de instrumentacion son cuellos de botella a proposito. El
 * store cubre las 13 secciones de reportes con una sola insercion, y
 * `ExportButtons` cubre toda la exportacion a Excel/PDF, incluida la de las
 * pantallas de patio que reutilizan el mismo componente. Instrumentar seccion
 * por seccion habria sido 26 ediciones para los mismos dos eventos.
 *
 * Privacidad: jamas viajan ids de dispositivo, nombres de unidad, placas,
 * coordenadas ni el nombre del archivo generado (el backend lo arma con datos
 * del cliente). Solo tipo de reporte, formato, dias de rango y conteos.
 */

const FEATURE = "reports";

/**
 * Convierte un rango de fechas a una cubeta de baja cardinalidad.
 *
 * Mandar el numero exacto de dias dispersa el desglose en decenas de valores;
 * la pregunta real es si la gente pide un dia, una semana, un mes o un
 * trimestre, que es lo que decide el costo del backend.
 */
export const bucketDateRange = (days: number): string => {
  if (!Number.isFinite(days) || days < 0) return "unknown";
  if (days <= 1) return "1d";
  if (days <= 7) return "7d";
  if (days <= 31) return "31d";
  if (days <= 90) return "90d";
  return "90d_plus";
};

/**
 * Reporte generado. Usa el evento `report_generated` de la fachada.
 *
 * `isBackground` separa los dos caminos reales del backend: descarga directa
 * contra reporte diferido. Son experiencias distintas y hoy no se puede
 * distinguir cual falla mas.
 */
export const trackReportGenerated = (params: {
  reportType: string;
  documentType: string;
  rangeDays: number;
  deviceCount: number;
  isBackground: boolean;
}): void => {
  void trackReportGeneration(params.reportType, bucketDateRange(params.rangeDays));
  void trackFeatureUsage(FEATURE, "report_generated", {
    report_type: params.reportType,
    document_type: params.documentType,
    date_range: bucketDateRange(params.rangeDays),
    device_count: params.deviceCount,
    is_background: params.isBackground,
  });
};

/**
 * Generacion fallida.
 *
 * `errorCode` debe ser un codigo HTTP o una etiqueta corta; nunca el mensaje
 * del backend, que puede traer el nombre del archivo o de la unidad.
 */
export const trackReportGenerationFailed = (params: {
  reportType: string;
  documentType: string;
  rangeDays: number;
  deviceCount: number;
  errorCode: string;
}): void => {
  void trackError("report_generation_failed", params.errorCode, "reports");
  void trackFeatureUsage(FEATURE, "report_generation_failed", {
    report_type: params.reportType,
    document_type: params.documentType,
    date_range: bucketDateRange(params.rangeDays),
    device_count: params.deviceCount,
    error_code: params.errorCode,
  });
};

/**
 * Exportacion del listado en pantalla a Excel o PDF.
 *
 * `rowCount` es la metrica que importa: las quejas de "se traba al exportar"
 * hoy no se pueden correlacionar con el tamano real de la tabla.
 */
export const trackReportExported = (params: {
  source: string;
  format: string;
  rowCount: number;
  columnCount: number;
}): void => {
  void trackFeatureUsage(FEATURE, "report_exported", {
    source: params.source,
    format: params.format,
    row_count: params.rowCount,
    column_count: params.columnCount,
  });
};

export const trackReportExportFailed = (params: {
  source: string;
  format: string;
  rowCount: number;
}): void => {
  void trackFeatureUsage(FEATURE, "report_export_failed", {
    source: params.source,
    format: params.format,
    row_count: params.rowCount,
  });
};
